/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Transition } from '@headlessui/react';
import useDropdownPosition from '../../hooks/useDropdownPosition';

export type Option = {
    id: string;
    name: string;
};

export default function TypeAhead({
    placeholder,
    label,
    options,
    caseSensitive,
    value,
    hideNoMatchMsg,
    onClick,
    onCleared,
    onChanged,
}: {
    placeholder: string;
    label?: string | undefined;
    options: Option[];
    caseSensitive?: boolean;
    // acceptNewValues?: boolean;
    value?: string | null;
    hideNoMatchMsg?: boolean;
    onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    onCleared?: () => void;
    onChanged?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [optionItems, setOptionItems] = useState([]);
    const [internalValue, setInternalValue] = useState(value);
    const { triggerRef, menuRef, position } = useDropdownPosition<HTMLDivElement>(isOpen);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideTrigger = triggerRef.current?.contains(target);
            const insideMenu = menuRef.current?.contains(target);
            if (!insideTrigger && !insideMenu) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (internalValue === undefined || internalValue !== value) {
            setInternalValue(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChanged) {
            onChanged(event);
        }
        let typedValue = event.target.value;

        setInternalValue(typedValue);

        if (typedValue.length > 0 && typedValue !== internalValue) {
            setIsOpen(true);
            setOptionItems([]);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const displayOptions: any = [];
            options.forEach((option: Option) => {
                let checkValue = option.name;
                if (!caseSensitive) {
                    checkValue = checkValue.toLowerCase();
                    typedValue = typedValue.toLowerCase();
                }
                if (checkValue.includes(typedValue)) {
                    const tempIndex = displayOptions
                        .map((optionItem: Option) => optionItem.name)
                        .indexOf(option.name);
                    if (tempIndex === -1 && displayOptions.length < 10) {
                        displayOptions.push(option);
                    }
                }
            });
            setOptionItems(displayOptions);
        } else {
            setIsOpen(false);
        }
    };

    /**
     * Primary function which triggers when the user clicks an option in the
     * TypeAhead search boc
     * @param event
     */
    const optionClicked = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const target = event.target as Element;
        const { id } = target;
        const index = optionItems.map((option: Option) => option.id).indexOf(id);
        const option = optionItems[index] as Option;
        setInternalValue(option.name);
        setIsOpen(false);
        onClick(event);
    };

    /**
     * Renders a list of options for the user to choose from
     */
    /**
     * With hideNoMatchMsg the menu is hidden outright when nothing matches,
     * rather than showing an empty panel.
     *
     * This is derived from state rather than calling setIsOpen(false) while
     * rendering, which is what it used to do. That was an unconditional
     * render-phase state update: React re-runs the render, the same call
     * happens again, and after 25 passes it throws "Too many re-renders",
     * which unmounts the tree and leaves a blank page. It only bit the fields
     * passing hideNoMatchMsg, and only once the typed text stopped matching
     * anything - typically a few characters in.
     */
    const hasVisibleOptions = optionItems.length > 0 || !hideNoMatchMsg;

    const renderOptions = () => {
        if (optionItems.length === 0) {
            return hideNoMatchMsg ? null : (
                <div className="text-sm text-foreground-muted px-3 py-2">No matches found</div>
            );
        }

        return optionItems.map((optionItem: Option) => (
            <div
                key={optionItem.id}
                id={optionItem.id}
                className="text-sm text-foreground cursor-pointer px-3 py-2 rounded-md hover:bg-surface-muted transition-colors"
                onClick={optionClicked}
            >
                {optionItem.name}
            </div>
        ));
    };

    const clearOptionItems = () => {
        setInternalValue('');
        if (onCleared) onCleared();
        setIsOpen(false);
    };

    return (
        <div>
            <div className="pb-0.5">
                <div>
                    {label && <div className="text-sm font-medium pb-1.5 text-label">{label}</div>}
                    <div
                        ref={triggerRef}
                        className="flex items-center border rounded-lg text-sm transition-colors bg-surface-muted border-border focus-within:bg-surface focus-within:border-border-strong"
                    >
                        <input
                            value={internalValue || ''}
                            type="text"
                            onChange={(e) => {
                                e.preventDefault();
                                handleSearch(e);
                            }}
                            placeholder={placeholder}
                            className="flex-1 min-w-0 bg-transparent pl-3 py-2 text-sm text-foreground placeholder-foreground-muted focus:outline-none"
                        />
                        <button
                            type="button"
                            onClick={() => clearOptionItems()}
                            className="flex items-center justify-center w-9 h-9 flex-shrink-0 text-foreground-muted hover:text-foreground transition-colors focus:outline-none"
                        >
                            <svg
                                className="w-4 h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            {position &&
                ReactDOM.createPortal(
                    <Transition
                        show={isOpen && hasVisibleOptions}
                        // The fixed positioning sits on the element that
                        // actually transitions, not on a child of it.
                        //
                        // Transition renders a wrapper div and applies the
                        // `transform` class to it for the length of the
                        // animation. A transformed element becomes the
                        // containing block for any fixed-position descendant,
                        // so while the animation ran the menu was positioned
                        // against this wrapper - which sits at the end of
                        // <body>, below the whole page - instead of against
                        // the viewport. That briefly stretched the document,
                        // flashed a scrollbar and shifted the layout sideways.
                        // An element's own transform does not affect its own
                        // containing block, so keeping both on one element
                        // animates without moving anything.
                        style={{
                            position: 'fixed',
                            top: position.top,
                            left: position.left,
                            width: position.width,
                            zIndex: 50,
                        }}
                        enter="transition ease-out duration-100 transform"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-75 transform"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div
                            ref={menuRef}
                            className="rounded-lg shadow-lg bg-surface border border-border max-h-60 overflow-y-auto py-1"
                        >
                            <div
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="options-menu"
                            >
                                {renderOptions()}
                            </div>
                        </div>
                    </Transition>,
                    document.body
                )}
        </div>
    );
}
